import {
  MinusIcon,
  PlusIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import {
  AspectRatio,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/joy";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import LoadingModal from "../../components/LoadingModal/LoadingModal";
import { theme } from "../../components/Theme";
import { getActiveBusiness } from "../../store/session";
import { calculatePrice } from "../../utils/cart";
import { getCountryInfo } from "../../utils/country";
import { formatPrice } from "../../utils/formatPrice";
import useContainer from "./useContainer";
import SupplierProductInformation from "./components/SupplierProductInformation";
import NotFound from "../NotFound/NotFound";
import dayjs from "dayjs";
import { If, IfElse } from "../../components/Condition";
import { CartButton } from "./components/CatalogueHeader";
import LogoImg from "../../assets/linkz-logo-small.png";
import LanguageSelector from "../../components/LanguageSelector";

function SupplierItemDetail() {
  const { t } = useTranslation();
  const isGuestMode = window.location.pathname.startsWith("/public");

  const { itemId } = useParams();
  const { catalogId } = useParams();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order");

  const [quantity, setQuantity] = useState(1);
  const [activeUOM, setActiveUOM] = useState<"uom1" | "uom2" | "uom3">("uom1");
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < theme.breakpoints.values.sm
  );

  const {
    isItemLoading,
    catalog,
    fetchCatalog,
    fetchCurrentItem,
    currentItem,
    handleUpdateCart,
    handleUpdateOrder,
    showToast,
    setShowToast,
    isLoading,
    cartItems,
  } = useContainer();

  useEffect(() => {
    if (window.innerWidth < theme.breakpoints.values.sm) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [window.innerWidth]);

  useEffect(() => {
    if (!isGuestMode) {
      const businessId = getActiveBusiness()?.id;
      if (!businessId) {
        return;
      }
    }

    if (catalogId && itemId) {
      // setIsLoading(true);
      fetchCatalog(catalogId, (_res) => fetchCurrentItem({ itemId }));
    }
  }, [catalogId, itemId, getActiveBusiness()?.id]);

  // memoize catalog's info, only recalculate if catalog changes
  const CATALOG_METADATA = useMemo(() => {
    const metadata = isMobile
      ? [
          // Total Items
          catalog?.itemsCount && {
            title: t("catalog.catalogTotalItems"),
            value: `${catalog.itemsCount} Items`,
          },

          // Last Updated
          {
            title: t("catalog.catalogLastUpdated"),
            value: dayjs(catalog?.updatedAt).format("YYYY-MM-DD"),
          },

          // Seller Location
          catalog?.business && {
            title: t("catalog.sellerLocation"),
            value: getCountryInfo(catalog.business.companyCountry)?.name,
          },
        ]
      : [
          // Total Items
          catalog?.itemsCount && {
            title: t("catalog.totalItems"),
            value: `${catalog.itemsCount} Items`,
          },

          // Seller Email
          catalog?.business && {
            title: t("catalog.sellerEmail"),
            value: catalog.business.companyEmail,
          },

          // Seller Location
          catalog?.business && {
            title: t("catalog.sellerLocation"),
            value: `${catalog.business.companyProvince}, ${
              getCountryInfo(catalog.business.companyCountry)?.name
            }`,
          },

          // Catalog Name
          catalog && {
            title: t("catalog.createCatalog.catalogueName"),
            value: `${catalog.catalogName}`,
          },

          // Seller Phone
          catalog?.business && {
            title: t("catalog.sellerPhone"),
            value: `+${catalog.business.companyNumberCountryCode} ${catalog.business.companyNumber}`,
          },

          // Last Updated
          {
            title: t("catalog.catalogLastUpdated"),
            value: dayjs(catalog?.updatedAt).format("YYYY-MM-DD"),
          },
        ];

    return metadata.filter(Boolean) as {
      title: string;
      value: string | ReactNode;
    }[];
  }, [catalog]);

  // const CatalogInfo = ({
  //   title,
  //   value,
  //   hiddenxs,
  // }: {
  //   title: string;
  //   value: string;
  //   hiddenxs: boolean;
  // }) => {
  //   if (hiddenxs) return null;
  //   return (
  //     <Grid lg={4} xs={4}>
  //       <div className="flex items-center justify-between">
  //         <div className="flex flex-col items-start justify-between lg:items-center lg:flex-row">
  //           <div className="flex justify-between gap-1 item-start lg:items-center">
  //             <span
  //               className={`text-[12px] text-[${theme.colorSchemes.light.palette.others.secondary_text}] w-[100px]`}
  //             >
  //               {title}
  //             </span>

  //             <span
  //               className={`hidden lg:block text-[12px] [${theme.colorSchemes.light.palette.others.secondary_text}]`}
  //             >
  //               {":"}
  //             </span>
  //           </div>
  //           <span className="text-[11px] font-semibold">&nbsp;{value}</span>
  //         </div>
  //       </div>
  //     </Grid>
  //   );
  // };

  if (isLoading || isItemLoading) {
    return <LoadingModal isLoading={isLoading || isItemLoading} />;
  }

  if (!currentItem || !catalog) {
    return <NotFound />;
  }

  return (
    <Stack
      spacing={2.5}
      sx={{
        px: isGuestMode
          ? {
              xs: "16px",
              lg: "32px",
            }
          : { lg: "8px" },
        py: {
          xs: "16px",
          lg: isGuestMode ? "32px" : 0,
        },
      }}
    >
      {/* <Stack
        direction={{ xs: "column", lg: "row" }}
        alignItems={{ xs: "flex-start", lg: "center" }}
        spacing={1}
        mt={4}
        sx={{
          background: "var(--Neutral-050, #F9FAFB)",
          paddingX: 2,
          paddingY: 3,
          // borderRadius: 8,
        }}
      >
        <div className="flex gap-2 w-[100%] lg:w-[25%] mb-2 lg:mb-0">
          <img
            style={{
              width: "60px",
              height: "60px",
              objectFit: "cover",
            }}
            src="/inventory-form-no-img.png"
            alt=""
          />
          <div>
            <Typography fontSize={16} fontWeight={500} textColor={"#101828"}>
              {catalog?.catalogName}
            </Typography>
            <If
              condition={
                (catalog.business as any).kycPaymentGatewayStatus ===
                "VALIDATED"
              }
            >
              <Typography fontSize={15} fontWeight={700} textColor={"#101828"}>
                {t("catalog.supplier.items.verifiedSupplier")}
              </Typography>
            </If>
          </div>
        </div>
        <Grid
          container
          lg={12}
          columnSpacing={{ md: 1 }}
          width={{ xs: "100%", lg: "75%" }}
        >
          <CatalogInfo
            title={t("catalog.totalItems")}
            value={(catalog.itemsCount || "0").toString() + ` Items`}
            hiddenxs={false}
          />
          <CatalogInfo
            title={t("catalog.sellerEmail")}
            value={catalog.business.companyEmail}
            hiddenxs={true}
          />
          <CatalogInfo
            title={t("catalog.sellerLocation")}
            value={`${catalog.business.companyProvince}, ${
              getCountryInfo(catalog.business.companyCountry)?.name
            }`}
            hiddenxs={false}
          />
          <CatalogInfo
            title={t("catalog.catalogLastUpdated")}
            value={
              catalog.updatedAt ? formatDate(new Date(catalog.updatedAt)) : ""
            }
            hiddenxs={false}
          />
          <CatalogInfo
            title={t("catalog.sellerPhone")}
            value={catalog.business.companyNumber}
            hiddenxs={true}
          />
        </Grid>
      </Stack> */}

      {/* guestmode header */}
      <If condition={isGuestMode}>
        <Stack
          direction={"row"}
          sx={{
            alignItems: "center",
            width: "100%",
            py: "1rem",
          }}
        >
          <img
            src={LogoImg}
            alt="linkz-logo"
            width={109.05}
            height={52}
            className={"mr-auto"}
          />

          <Stack
            direction={"row"}
            sx={{
              gap: "0.5rem",
              ml: "auto",
            }}
          >
            <LanguageSelector />

            <Link
              to={`/signin?callbackURL=/client/suppliers/${catalogId}/items`}
              className="ml-3"
            >
              <Button variant="primary-light" className="min-w-[3rem]">
                {t("landing.loginBtn")}
              </Button>
            </Link>
          </Stack>
        </Stack>
      </If>

      <Stack
        direction={{ xs: "column", md: "row" }}
        sx={{
          width: { xs: "calc(100% + 32px) !important", md: "100%" },
          mt: "32px !important",
          background: "var(--Neutral-050, #F9FAFB)",
          p: "16px",
          gap: "16px",
          ml: "-16px !important",
        }}
      >
        {/* catalogue header */}
        <Stack
          direction={"row"}
          sx={{
            alignItems: "center",
            gap: { xs: "8px", md: "16px" },
          }}
        >
          {/* company image */}
          <Box width={{ xs: "32px", md: "60px" }}>
            <AspectRatio
              ratio={1}
              sx={{
                width: { xs: "32px", md: "60px" },
                height: { xs: "32px", md: "60px" },
                objectFit: "cover",
                borderRadius: { xs: "2.13px", md: "4px" },
              }}
            >
              <img
                src={
                  import.meta.env.VITE_API_URL +
                  "/objects/" +
                  catalog.business.id
                }
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/default-company-image.png";
                }}
                alt="company-logo"
              />
            </AspectRatio>
          </Box>

          {/* catalogue name & company name */}
          <Stack
            direction={"column"}
            sx={{
              justifyContent: "center",
            }}
          >
            {/* web large company name */}
            <Typography
              level={"title-lg"}
              textColor={theme.palette.common.black}
              sx={{
                display: { xs: "none", md: "flex" },
              }}
            >
              {catalog?.business?.companyName}
            </Typography>

            {/* web mobile company name */}
            <Typography
              level={"text-xs-bold"}
              textColor={theme.palette.common.black}
              sx={{
                display: { xs: "flex", md: "none" },
              }}
            >
              {catalog?.business?.companyName}
            </Typography>

            {/* web large verified */}
            <Typography
              level={"text-md-medium"}
              textColor={theme.palette.common.black}
              sx={{
                display: { xs: "none", md: "flex" },
              }}
            >
              {t("catalog.supplier.items.verifiedSupplier")}
            </Typography>

            {/* web mobile verified */}
            <Typography
              level={"text-xs-medium"}
              textColor={theme.palette.common.black}
              sx={{
                display: { xs: "flex", md: "none" },
              }}
            >
              {t("catalog.supplier.items.verifiedSupplier")}
            </Typography>
          </Stack>

          <Box
            sx={{
              ml: "auto",
              display: { xs: "flex", md: "none" },
            }}
          >
            <CartButton catalog={catalog} cartItems={cartItems} />
          </Box>
        </Stack>

        {/* catalog metadata */}
        <Grid
          container
          lg={12}
          columnSpacing={{ md: 1 }}
          rowGap={"4px"}
          width={"100%"}
        >
          {CATALOG_METADATA.map(({ title, value }) => (
            <Grid xs={12 / 3} lg={12 / 3}>
              <Stack direction={{ xs: "column", md: "row" }}>
                <Typography
                  level="text-xs-medium"
                  textColor={theme.palette.others.secondary_text}
                  width={{ xs: "100%", md: "45%" }}
                >
                  {title}
                </Typography>

                <IfElse
                  condition={typeof value === "string"}
                  ifBlock={
                    <>
                      {/* web large */}
                      <Typography
                        level="text-xs-semibold"
                        textColor={theme.palette.common.black}
                        sx={{
                          display: { xs: "none", md: "flex" },
                        }}
                      >
                        : {value}
                      </Typography>

                      {/* web mobile */}
                      <Typography
                        level="text-xs-semibold"
                        textColor={theme.palette.common.black}
                        sx={{
                          display: { xs: "flex", md: "none" },
                        }}
                      >
                        {value}
                      </Typography>
                    </>
                  }
                  elseBlock={value}
                />
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Stack>

      {/* image and description */}
      <SupplierProductInformation
        catalog={catalog}
        setActiveUOM={setActiveUOM}
        activeUOM={activeUOM}
        item={currentItem}
      />

      {/* product specification */}
      <Box
        sx={{
          p: { xs: 0, lg: 2 },
          border: { xs: 0, lg: "1px solid" },
          borderRadius: "8px",
          borderColor: { lg: theme.palette.neutral[200] },
        }}
        // className="p-0 border-0 rounded-md lg:border lg:p-2"
      >
        <Typography
          level="text-md-bold"
          sx={{
            borderRadius: "4px",
            backgroundColor: theme.palette.neutral[50],
            padding: "10px 15px",
            width: "100%",
            color: theme.palette.common.black,
          }}
        >
          {t("catalog.supplier.items.productSpecification")}
        </Typography>

        <Grid
          container
          columns={12}
          spacing={"8px"}
          sx={{
            width: "100%",
            my: "20px",
            mx: 0,
          }}
          // className="flex flex-col gap-2 my-5"
        >
          {currentItem.inventoryItem.flexiColumns?.data.map((col) => (
            <>
              <Grid xs={3} lg={1}>
                <Typography
                  level="text-sm-regular"
                  textColor={theme.colorSchemes.light.palette.common.black}
                  // className={`text-[${theme.colorSchemes.light.palette.common.white}] text-sm w-[100px]`}
                >
                  {col.columnName}
                </Typography>
              </Grid>
              <Grid xs={9} lg={11}>
                <Typography
                  level="text-sm-semibold"
                  textColor={theme.colorSchemes.light.palette.common.black}
                  // className={`text-[${theme.colorSchemes.light.palette.common.black}] font-semibold`}
                >
                  {col.cellValue}
                </Typography>
              </Grid>
            </>
          ))}

          {/* tags */}
          <Grid xs={3} lg={1}>
            <Typography
              level="text-sm-regular"
              textColor={theme.colorSchemes.light.palette.common.black}
              // className={`text-[${theme.colorSchemes.light.palette.common.white}] text-sm w-[100px]`}
            >
              {t("catalog.inventory.editForm.tags")}
            </Typography>
          </Grid>
          <Grid xs={9} lg={11}>
            <Stack direction={"row"} gap={"8px"}>
              {currentItem.inventoryItem.tags?.tags.map((tag) => {
                return (
                  <Chip
                    sx={{
                      backgroundColor: theme.palette.others.carousel_index_bg,
                      zIndex: 100,
                      py: "4px",
                      px: "8px",
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      level="text-xs-regular"
                      textColor={theme.palette.neutral[600]}
                    >
                      {tag}
                    </Typography>
                  </Chip>
                );
              })}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* product description */}
      <Box
        sx={{
          p: { xs: 0, lg: 2 },
          border: { xs: 0, lg: "1px solid" },
          borderRadius: "8px",
          borderColor: { lg: theme.palette.neutral[200] },
        }}
        // className="p-0 border-0 rounded-md lg:border lg:p-2"
      >
        <Typography
          level="text-md-bold"
          sx={{
            borderRadius: "4px",
            backgroundColor: theme.palette.neutral[50],
            padding: "10px 15px",
            width: "100%",
            color: theme.palette.common.black,
          }}
        >
          {t("catalog.supplier.items.productDescription")}
        </Typography>

        <Typography
          level="text-sm-regular"
          sx={{ whiteSpace: "pre-line", mt: "24px" }}
        >
          {currentItem.inventoryItem.productDescription}
        </Typography>

        {/* <Typography
        fontSize={15}
        fontWeight={400}
        sx={{
          color: "#000",
          margin: "30px 0px",
        }}
      >
        This item has been restock again and can be available soon
      </Typography> */}
      </Box>

      {/* quantity and add to cart button */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
        }}
        // className="block lg:hidden"
      >
        {/* quantity controller input */}
        <Stack
          direction="row"
          // className="mt-4 mb-2 overflow-hidden border border-gray-400 rounded-lg"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mt: 4,
            mb: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.neutral[400],
            borderRadius: "8px",
            minHeight: "48px",
          }}
        >
          <IconButton
            variant="plain"
            onClick={() => setQuantity(quantity - 1 > 0 ? quantity - 1 : 1)}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 0,
              borderRight: `1px solid ${theme.palette.neutral[400]}`,
              height: "48px",
              width: "40px",
            }}
          >
            <MinusIcon
              width={"24px"}
              height={"24px"}
              color={theme.palette.primary[500]}
            />
          </IconButton>

          <Typography
            level="text-md-semibold"
            className="w-[60px] flex justify-center items-center"
          >
            {quantity}
          </Typography>

          <IconButton
            variant="plain"
            onClick={() => setQuantity(quantity + 1)}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 0,
              borderLeft: `1px solid ${theme.palette.neutral[400]}`,
              height: "48px",
              width: "40px",
            }}
          >
            <PlusIcon
              width={"24px"}
              height={"24px"}
              color={theme.palette.primary[500]}
            />
          </IconButton>
        </Stack>

        <Stack
          direction={"row"}
          spacing="10px"
          alignItems={"center"}
          sx={{
            color: "#475467",
            borderRadius: "4px",
            padding: "10px 0px",
            width: "100%",
          }}
        >
          <Typography
            fontSize={14}
            fontWeight={500}
            sx={{
              color: "#475467",
            }}
          >
            {t("catalog.supplier.items.totalPrice")}
          </Typography>
          <Typography
            fontSize={15}
            fontWeight={600}
            sx={{
              color: "#000",
            }}
          >
            {`${
              catalog.currency ??
              currentItem.currency ??
              currentItem.inventoryItem.currency
            } ${
              isGuestMode
                ? catalog.catalogType === "PRIVATE"
                  ? "-"
                  : formatPrice(
                      calculatePrice(currentItem, activeUOM) * quantity,
                      catalog.currency ??
                        currentItem.currency ??
                        currentItem.inventoryItem.currency
                    )
                : formatPrice(
                    calculatePrice(currentItem, activeUOM) * quantity,
                    catalog.currency ??
                      currentItem.currency ??
                      currentItem.inventoryItem.currency
                  )
            }`}
          </Typography>
        </Stack>

        {/* add to cart button */}
        <Button
          fullWidth
          sx={{
            borderRadius: "8px",
          }}
          onClick={() => {
            if (orderId) {
              handleUpdateOrder(
                orderId,
                catalog,
                currentItem,
                quantity,
                activeUOM
              );
            } else {
              handleUpdateCart(
                catalogId ?? "",
                activeUOM,
                currentItem,
                quantity
              );
            }

            setShowToast(true);
            setTimeout(() => {
              setShowToast(false);
            }, 3000);
          }}
        >
          <ShoppingCartIcon
            style={{
              width: 18,
              height: 18,
              marginRight: "0.5rem",
            }}
          />
          {t(`catalog.addToCartBtn`)}
        </Button>

        {/* <button
        onClick={() => {
          handleUpdateCart(catalogId ?? "", activeUOM, currentItem, quantity);
        }}
        className={`flex justify-center items-center px-4 py-3 my-4 w-full text-white capitalize rounded-lg border cursor-default lg:my-0 bg-primary-500`}
      >
        <ShoppingCartIcon
          style={{
            width: 18,
            height: 18,
            marginRight: "0.5rem",
          }}
        />
        {t(`catalog.addToCartBtn`)}
      </button> */}

        <Snackbar
          variant="solid"
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={showToast}
          key={"asjfkangdlknakjrsh"}
          sx={{
            width: "40%",
            maxWidth: 800,
          }}
        >
          <Box width={"100%"}>
            <Typography
              textAlign={"center"}
              textColor={"common.white"}
              fontSize={14}
            >
              {t(`catalog.${orderId ? "itemAddedToOrder" : "itemAddedToCart"}`)}
            </Typography>
          </Box>
        </Snackbar>
      </Box>
    </Stack>
  );
}

export default SupplierItemDetail;
